'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/image-with-fallback/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { DEFAULT_IMAGE_FALLBACK, hasFallbackIcon, toInitials } from 'BomdisoftComponents/image-with-fallback/render';
import Image from 'next/image';
import { ReactNode, useEffect, useState } from 'react';

type ImageWithFallbackParams = {
  src?: string | null;
  alt: string;
  fill?: boolean;
  height?: number;
  width?: number;
  priority?: boolean;
  sizes?: string;
  fallback?: string;
  fallbackIcon?: ReactNode;
  className?: string;
};

export const ImageWithFallback = ({ src, fill = false, height, width, priority, sizes, fallbackIcon, fallback = DEFAULT_IMAGE_FALLBACK, alt, className }: ImageWithFallbackParams) => {
  const [error, setError] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <necessary to reset error state>
  useEffect(() => {
    setError(false);
  }, [src]);

  const handleError = () => setError(true);

  if (!src || (!fill && (!height || !width))) {
    return (
      <div className={joinClasses('bomdisoft-image-with-fallback-avatar', className)} data-testid='iwf-avatar'>
        {toInitials(alt)}
      </div>
    );
  }

  const withIcon = hasFallbackIcon(fallbackIcon);
  const shared = {
    alt,
    className: joinClasses('bomdisoft-image-with-fallback-image', className),
    'data-bomdisoft-fallback': withIcon ? undefined : fallback,
    'data-bomdisoft-fallback-icon': withIcon ? '' : undefined,
    'data-fill': fill ? '' : undefined,
    'data-testid': 'iwf-image',
    hidden: withIcon && error ? true : undefined,
    onError: handleError,
    priority,
    src: error && !withIcon ? fallback : src,
  };
  const image = fill ? <Image {...shared} fill sizes={sizes} /> : <Image {...shared} height={height} width={width} />;

  if (!withIcon) return image;

  return (
    <>
      {image}
      <span className='bomdisoft-image-with-fallback-icon' hidden={!error}>
        {fallbackIcon}
      </span>
    </>
  );
};
